import { faLink } from '@fortawesome/free-solid-svg-icons/faLink';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faSave } from '@fortawesome/free-solid-svg-icons/faSave';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraIcon } from '../../../components/AtiraIcon';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Input } from '../../../components/Input';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { TextWithTruncated } from '../../../components/TextWithTruncated';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { Lengths } from '../../../model/shared/enum/Lengths.enum';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { Tooltip } from '../../../utils/local-antd';
import { LinkInput } from './LinkInput';

const CustomLinkInputsWrapper = styled(Flex)`
  gap: ${Spacing.s};
  flex-direction: column;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    flex-direction: row;
  }
`;

const AddCustomLinkButton = styled(Button)`
  height: 2.3rem;
  width: fit-content;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    height: 2.7rem;
  }
`;

const CustomLinksWrapper = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.s};
  margin-top: ${Spacing.s};
`;

const CustomLinkHandleWrapper = styled(Flex)<{ valid?: boolean }>`
  align-items: center;
  width: 100%;
  gap: ${Spacing.s};
  padding: ${Spacing.s};
  border-radius: ${Rounded.lg};
  border: ${({ theme, valid }) =>
    `2px solid ${theme[valid ? 'danger' : 'main']}`};
  flex-direction: column;

  @media (min-width: ${Breakpoints.TABLET}) {
    flex-direction: row;
  }
`;

const CustomLinksSection = styled(Flex)`
  width: 100%;
  gap: ${Spacing.l};
  flex-wrap: wrap;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    gap: ${Spacing.l};
  }
`;

const CustomLinkWrapper = styled(Flex)`
  flex-direction: column;
  width: 100%;

  & > div:first-child {
    flex-direction: column;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    & > div:first-child {
      flex-direction: row;
    }
  }
`;

const ActionsWrapper = styled(Flex)`
  gap: ${Spacing.s};
  align-items: center;
  margin-inline-start: auto;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    flex-direction: column;
  }
`;

const CustomHandleLinkWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 1px;
`;

const StyledButton = styled(Button)`
  padding: ${Spacing.s};
`;

const LinkWrapper = styled(Flex)`
  padding-inline-start: calc(26.25px + ${Spacing.s}) !important;
`;

const StyledInput = styled(Input)`
  background-color: ${({ theme }) => theme.transparent};
  border: ${({ theme }) => `1px solid ${theme.fgPrimary}`} !important;
  color: ${({ theme }) => theme.fgPrimary};
`;

const EditableTitleInput = styled(StyledInput)`
  font-weight: bold;
  margin-bottom: 2px;
`;

export const AtiraLinkContentCustomLinks: React.FC = () => {
  const [customLinkLabel, setCustomLinkLabel] = useState('');
  const [customLinkURL, setCustomLinkURL] = useState('');
  const [customLinkLabelError, setCustomLinkLabelError] = useState(false);
  const [customLinkURLError, setCustomLinkURLError] = useState(false);
  const [editingCustomLinkID, setEditingCustomLinkID] = useState<string | null>(
    null,
  );

  const titleInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const { t } = useTranslation();

  const {
    control,
    getValues,
    watch,
    formState: { errors },
    clearErrors,
  } = useFormContext<CreateAtiraLinkDto>();

  const {
    fields: customLinks,
    remove: removeCustomLink,
    append: appendCustomLink,
    update: updateCustomLink,
  } = useFieldArray({
    control,
    name: 'customLinks',
  });

  const isValidCustomLink = (index: number) => {
    const customLink = errors?.customLinks?.[index];

    return (
      customLink &&
      Boolean(
        customLink?.label?.message?.length ||
          customLink?.value?.message?.length,
      )
    );
  };

  const onAddCustomLink = () => {
    if (!customLinkLabel.trim()) {
      setCustomLinkLabelError(true);
    } else {
      setCustomLinkLabelError(false);
    }

    if (!customLinkURL.trim()) {
      setCustomLinkURLError(true);
    } else {
      setCustomLinkURLError(false);
    }

    if (customLinkLabel.trim() && customLinkURL.trim()) {
      appendCustomLink({ label: customLinkLabel, value: customLinkURL });
      setCustomLinkLabel('');
      setCustomLinkURL('');
    }
  };

  const onRemoveCustomLink = (index: number) => {
    removeCustomLink(index);
    setCustomLinkLabelError(false);
    setCustomLinkURLError(false);
  };

  const onEditCustomLink = (index: number) => {
    const liveCustomLinks = getValues('customLinks');

    const customLink = liveCustomLinks?.[index];

    if (!customLink) {
      return;
    }

    if (customLink.label.trim() && customLink.value.trim()) {
      updateCustomLink(index, {
        label: customLink.label,
        value: customLink.value,
      });
    }
  };

  useEffect(() => {
    if (editingCustomLinkID && titleInputRefs.current[editingCustomLinkID]) {
      titleInputRefs.current[editingCustomLinkID]?.focus();
    }
  }, [editingCustomLinkID]);

  return (
    <Section title={t('common.custom_links')}>
      <CustomLinksSection>
        <CustomLinkWrapper>
          <Text>{t('atira_link.create.content.custom-links.label.info')}</Text>

          <CustomLinkInputsWrapper>
            <LinkInput
              title={`${t('atira_link.create.content.custom-links.label')}:`}
              value={customLinkLabel}
              onChange={(e) => setCustomLinkLabel(e.currentTarget.value)}
              placeholder={t(
                'atira_link.create.content.social_label.plcaeholder',
              )}
              maxLength={Lengths.NAME}
              dataTestId={atiraLinkTestIDs.content.customLinks.label}
              valid={!customLinkLabelError}
            />

            <LinkInput
              title={`${t('atira_link.create.content.custom-links.value')}:`}
              valid={!customLinkURLError}
              value={customLinkURL}
              onChange={(e) => setCustomLinkURL(e.currentTarget.value)}
              placeholder="https://yourlink.com"
              maxLength={Lengths.DESCRIPTION}
              dataTestId={atiraLinkTestIDs.content.customLinks.value}
              type="url"
            />

            <Tooltip title={t('atira_link.create.custom_link.add')}>
              <AddCustomLinkButton
                icon={faPlus}
                onClick={onAddCustomLink}
                disabled={customLinks && customLinks.length >= 2}
                dataTestId={atiraLinkTestIDs.content.customLinks.add}
              />
            </Tooltip>
          </CustomLinkInputsWrapper>

          {customLinks?.length ? (
            <CustomLinksWrapper>
              <Text fontWeight={'bold'}>
                {t('atira_link.create.custom_link.custom_links.title')}
              </Text>

              {customLinks.map((item, index) => (
                <Flex
                  key={item.id}
                  flexDirection="column"
                  id={`custom-link-${item.label}-${item.value}`}
                >
                  <CustomLinkHandleWrapper valid={isValidCustomLink(index)}>
                    <CustomHandleLinkWrapper>
                      <Flex alignItems="center" gap="s">
                        <AtiraIcon icon={faLink} size="xl" color="fgPrimary" />

                        {editingCustomLinkID === item.id ? (
                          <Controller
                            control={control}
                            name={`customLinks.${index}.label`}
                            render={({
                              field: { value, onChange, ref },
                              fieldState: { error },
                            }) => (
                              <EditableTitleInput
                                value={value}
                                onChange={onChange}
                                valid={!error}
                                ref={(el) => {
                                  titleInputRefs.current[item.id] = el;
                                  ref(el);
                                }}
                                onFocus={() =>
                                  clearErrors(`customLinks.${index}.label`)
                                }
                              />
                            )}
                          />
                        ) : (
                          <TextWithTruncated
                            length={20}
                            textProps={{
                              fontWeight: '600',
                              wordBreak: 'break-word',
                              fontSize: 's',
                            }}
                          >
                            {item.label}
                          </TextWithTruncated>
                        )}
                      </Flex>

                      <LinkWrapper>
                        {editingCustomLinkID === item.id ? (
                          <Controller
                            control={control}
                            name={`customLinks.${index}.value`}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <StyledInput
                                value={value}
                                onChange={onChange}
                                valid={!error}
                                onFocus={() =>
                                  clearErrors(`customLinks.${index}.value`)
                                }
                                type="url"
                              />
                            )}
                          />
                        ) : (
                          <TextWithTruncated
                            length={70}
                            textProps={{
                              wordBreak: 'break-all',
                              fontSize: 'xs',
                            }}
                          >
                            {item.value}
                          </TextWithTruncated>
                        )}
                      </LinkWrapper>
                    </CustomHandleLinkWrapper>

                    <ActionsWrapper>
                      <Tooltip
                        title={
                          editingCustomLinkID === item.id
                            ? t('common.save')
                            : t('common.edit')
                        }
                      >
                        <StyledButton
                          onClick={() =>
                            editingCustomLinkID === item.id
                              ? onEditCustomLink(index)
                              : setEditingCustomLinkID(item.id)
                          }
                          icon={
                            editingCustomLinkID === item.id ? faSave : faPen
                          }
                          disabled={
                            editingCustomLinkID === item.id &&
                            (!watch(`customLinks.${index}.label`) ||
                              !watch(`customLinks.${index}.value`))
                          }
                        />
                      </Tooltip>

                      {editingCustomLinkID !== item.id ? (
                        <Tooltip
                          title={t(
                            'atira_link.create.custom_link.remove.tooltip',
                          )}
                        >
                          <StyledButton
                            onClick={() => onRemoveCustomLink(index)}
                            icon={faTrash}
                            dataTestId={atiraLinkTestIDs.content.customLinks.remove(
                              item.id,
                            )}
                          />
                        </Tooltip>
                      ) : null}
                    </ActionsWrapper>
                  </CustomLinkHandleWrapper>

                  {Boolean(errors.customLinks?.[index]?.value?.message) ? (
                    <Text fontSize="s" color="danger">
                      {errors?.customLinks?.[index]?.value?.message}
                    </Text>
                  ) : null}
                </Flex>
              ))}
            </CustomLinksWrapper>
          ) : null}
        </CustomLinkWrapper>
      </CustomLinksSection>
    </Section>
  );
};
